// AI Recipe Generator - Generate recipes based on available ingredients
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChefHat, Plus, X, Sparkles, Loader2, Clock, Flame, Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Common ingredients for quick selection
const COMMON_INGREDIENTS = [
  "Poulet", "Œufs", "Riz", "Pâtes", "Tomates", "Oignons", "Ail",
  "Courgettes", "Poivrons", "Carottes", "Pommes de terre", "Saumon",
  "Bœuf", "Fromage", "Lait", "Crème", "Citron", "Champignons"
];

export default function RecipeGenerator({ onRecipeGenerated, language = 'fr' }) {
  const [open, setOpen] = useState(false);
  const [ingredients, setIngredients] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedRecipe, setGeneratedRecipe] = useState(null);
  const [preferences, setPreferences] = useState({
    diet: 'normal', // normal, vegetarian, vegan, keto
    time: 30, // minutes
    servings: 2
  });
  
  const addIngredient = (ingredient) => {
    const trimmed = ingredient.trim();
    if (trimmed && !ingredients.includes(trimmed)) {
      setIngredients([...ingredients, trimmed]);
      setInputValue('');
    }
  };
  
  const removeIngredient = (ingredient) => {
    setIngredients(ingredients.filter(i => i !== ingredient));
  };
  
  const generateRecipe = async () => {
    if (ingredients.length < 2) {
      toast.error(language === 'fr' ? 'Ajoutez au moins 2 ingrédients' : 'Add at least 2 ingredients');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/ai/generate-recipe`, {
        ingredients,
        preferences
      }, { withCredentials: true });
      
      setGeneratedRecipe(response.data);
      onRecipeGenerated?.(response.data);
    } catch (error) {
      // Fallback recipe generation
      const fallbackRecipe = generateFallbackRecipe(ingredients, preferences);
      setGeneratedRecipe(fallbackRecipe);
    } finally {
      setLoading(false);
    }
  };
  
  // Simple fallback recipe generator
  const generateFallbackRecipe = (ingr, prefs) => {
    const hasProtein = ingr.some(i => ['poulet', 'bœuf', 'saumon', 'œufs', 'tofu'].includes(i.toLowerCase()));
    const hasCarb = ingr.some(i => ['riz', 'pâtes', 'pommes de terre', 'pain'].includes(i.toLowerCase()));
    
    let recipeName = "Poêlée maison";
    let instructions = [];
    
    if (hasProtein && hasCarb) {
      recipeName = `Bowl ${ingr[0]} et ${ingr.find(i => ['riz', 'pâtes'].includes(i.toLowerCase())) || 'légumes'}`;
      instructions = [
        "Coupez tous les ingrédients en morceaux.",
        "Faites cuire la protéine dans une poêle avec un filet d'huile.",
        "Ajoutez les légumes et faites revenir 5 minutes.",
        "Servez sur un lit de féculents.",
        "Assaisonnez selon vos goûts."
      ];
    } else {
      recipeName = `Poêlée de ${ingr.slice(0, 2).join(' et ')}`;
      instructions = [
        "Préparez et coupez tous vos ingrédients.",
        "Chauffez une poêle avec de l'huile d'olive.",
        "Faites revenir les ingrédients les plus longs à cuire en premier.",
        "Ajoutez les autres ingrédients progressivement.",
        "Assaisonnez avec sel, poivre et herbes de votre choix.",
        "Servez chaud."
      ];
    }
    
    return {
      name: recipeName,
      ingredients: ingr.map(i => ({ name: i, quantity: "À ajuster" })),
      instructions,
      prep_time: `${prefs.time} min`,
      servings: prefs.servings,
      calories: Math.round(200 + ingr.length * 50),
      protein: Math.round(10 + (hasProtein ? 20 : 5)),
      carbs: Math.round(20 + (hasCarb ? 30 : 10)),
      fat: 12,
      tips: "N'hésitez pas à ajuster les quantités selon vos goûts !"
    };
  };
  
  const resetGenerator = () => {
    setIngredients([]);
    setGeneratedRecipe(null);
    setInputValue('');
  };
  
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetGenerator(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2">
          <ChefHat className="w-4 h-4" />
          {language === 'fr' ? 'Générer une recette' : 'Generate Recipe'}
        </Button>
      </DialogTrigger>
      
      <DialogContent className="max-w-md max-h-[90vh] p-0">
        <DialogHeader className="p-4 pb-2 bg-gradient-to-r from-orange-500/10 to-amber-500/10">
          <DialogTitle className="flex items-center gap-2">
            <ChefHat className="w-5 h-5 text-orange-500" />
            {language === 'fr' ? 'Chef IA - Créez votre recette' : 'AI Chef - Create your recipe'}
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="max-h-[70vh]">
          <div className="p-4 space-y-4">
            {!generatedRecipe ? (
              <>
                {/* Ingredient input */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    {language === 'fr' ? 'Vos ingrédients disponibles' : 'Your available ingredients'}
                  </label>
                  <div className="flex gap-2">
                    <Input
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder={language === 'fr' ? "Ex: Poulet, Riz..." : "Ex: Chicken, Rice..."}
                      onKeyDown={(e) => e.key === 'Enter' && addIngredient(inputValue)}
                    />
                    <Button size="icon" onClick={() => addIngredient(inputValue)}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Selected ingredients */}
                {ingredients.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {ingredients.map((ing, i) => (
                      <Badge key={i} variant="secondary" className="pl-2 pr-1 py-1 gap-1">
                        {ing}
                        <button onClick={() => removeIngredient(ing)} className="hover:bg-white/20 rounded-full p-0.5">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                
                {/* Quick add common ingredients */}
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Ajout rapide :</p>
                  <div className="flex flex-wrap gap-1">
                    {COMMON_INGREDIENTS.filter(i => !ingredients.includes(i)).slice(0, 12).map((ing, i) => (
                      <button
                        key={i}
                        onClick={() => addIngredient(ing)}
                        className="text-xs px-2 py-1 rounded-full bg-muted hover:bg-muted/80 transition-colors"
                      >
                        + {ing}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Preferences */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground">Temps max</label>
                    <select 
                      className="w-full mt-1 p-2 rounded-lg bg-muted text-sm"
                      value={preferences.time}
                      onChange={(e) => setPreferences({...preferences, time: parseInt(e.target.value)})}
                    >
                      <option value={15}>15 min</option>
                      <option value={30}>30 min</option>
                      <option value={45}>45 min</option>
                      <option value={60}>1h</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Portions</label>
                    <select 
                      className="w-full mt-1 p-2 rounded-lg bg-muted text-sm"
                      value={preferences.servings}
                      onChange={(e) => setPreferences({...preferences, servings: parseInt(e.target.value)})}
                    >
                      <option value={1}>1 pers.</option>
                      <option value={2}>2 pers.</option>
                      <option value={4}>4 pers.</option>
                      <option value={6}>6 pers.</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Régime</label>
                    <select 
                      className="w-full mt-1 p-2 rounded-lg bg-muted text-sm"
                      value={preferences.diet}
                      onChange={(e) => setPreferences({...preferences, diet: e.target.value})}
                    >
                      <option value="normal">Normal</option>
                      <option value="vegetarian">Végétarien</option>
                      <option value="vegan">Vegan</option>
                      <option value="keto">Keto</option>
                    </select>
                  </div>
                </div>
                
                {/* Generate button */}
                <Button 
                  className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:opacity-90"
                  onClick={generateRecipe}
                  disabled={ingredients.length < 2 || loading}
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Génération...</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" /> Générer ma recette</>
                  )}
                </Button>
              </>
            ) : (
              /* Generated recipe display */
              <div className="space-y-4">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-r from-orange-500 to-amber-500 flex items-center justify-center mb-3">
                    <ChefHat className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold">{generatedRecipe.name}</h3>
                </div>
                
                {/* Recipe stats */}
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="p-2 rounded-lg bg-muted">
                    <Clock className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                    <p className="text-xs">{generatedRecipe.prep_time}</p>
                  </div>
                  <div className="p-2 rounded-lg bg-muted">
                    <Users className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                    <p className="text-xs">{generatedRecipe.servings} pers.</p>
                  </div>
                  <div className="p-2 rounded-lg bg-muted">
                    <Flame className="w-4 h-4 mx-auto mb-1 text-orange-500" />
                    <p className="text-xs">{generatedRecipe.calories} kcal</p>
                  </div>
                  <div className="p-2 rounded-lg bg-muted">
                    <span className="text-xs font-bold text-primary">{generatedRecipe.protein}g</span>
                    <p className="text-xs text-muted-foreground">Prot.</p>
                  </div>
                </div>
                
                {/* Ingredients list */}
                <Card>
                  <CardContent className="p-3">
                    <h4 className="font-medium text-sm mb-2">Ingrédients</h4>
                    <ul className="text-sm space-y-1">
                      {generatedRecipe.ingredients.map((ing, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                          {ing.name} {ing.quantity !== "À ajuster" && `- ${ing.quantity}`}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
                
                {/* Instructions */}
                <Card>
                  <CardContent className="p-3">
                    <h4 className="font-medium text-sm mb-2">Préparation</h4>
                    <ol className="text-sm space-y-2">
                      {generatedRecipe.instructions.map((step, i) => (
                        <li key={i} className="flex gap-2">
                          <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center flex-shrink-0">
                            {i + 1}
                          </span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ol>
                  </CardContent>
                </Card>
                
                {generatedRecipe.tips && (
                  <p className="text-xs text-muted-foreground text-center italic">
                    💡 {generatedRecipe.tips}
                  </p>
                )}
                
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={resetGenerator}>
                    Nouvelle recette
                  </Button>
                  <Button className="flex-1" onClick={() => { toast.success('Recette sauvegardée !'); setOpen(false); }}>
                    Sauvegarder
                  </Button>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
